<template>
  <IntercomModal
    v-model:show="showModal"
    title="Record Payment"
    width="500px"
  >
    <!-- Bill Details -->
    <div v-if="bill" class="bill-details">
      <div class="bill-detail-row">
        <span class="label">{{ $t('payments.invoiceNumber') }}:</span>
        <span class="value">{{ bill.bill_number }}</span>
      </div>
      <div class="bill-detail-row">
        <span class="label">Vendor:</span>
        <span class="value">{{ bill.vendor_name }}</span>
      </div>
      <div class="bill-detail-row">
        <span class="label">Total Amount:</span>
        <span class="value">&#8377;{{ (bill.total_amount || 0).toFixed(2) }}</span>
      </div>
      <div class="bill-detail-row">
        <span class="label">{{ $t('payments.totalPaid') }}:</span>
        <span class="value">&#8377;{{ (bill.amount_paid || 0).toFixed(2) }}</span>
      </div>
      <div class="bill-detail-row" :class="{ highlight: amountDue > 0 }">
        <span class="label">{{ $t('payments.amountDue') }}:</span>
        <span class="value">&#8377;{{ amountDue.toFixed(2) }}</span>
      </div>
    </div>

    <div class="payment-form">
      <IntercomInput
        v-model.number="formData.amount"
        type="number"
        :label="$t('payments.amount')"
        :placeholder="$t('payments.amount')"
        step="0.01"
        required
        :error="errors.amount"
      />

      <IntercomSelect
        v-model="formData.payment_mode"
        :label="$t('payments.paymentMode') + ' *'"
        :options="paymentModeOptions"
      />

      <IntercomInput
        v-model="formData.reference_number"
        :label="$t('payments.referenceNumber')"
        :placeholder="$t('payments.referenceNumber')"
      />

      <IntercomInput
        v-model="formData.notes"
        type="textarea"
        :label="$t('payments.notes')"
        :placeholder="$t('payments.notes')"
        :rows="2"
      />
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        Record Payment
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal, IntercomInput, IntercomButton, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  bill: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createPurchasePayment } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)

// Computed amount due
const amountDue = computed(() => {
  if (!props.bill) return 0
  return (props.bill.total_amount || 0) - (props.bill.amount_paid || 0)
})

// Payment mode options
const paymentModeOptions = computed(() => [
  { label: t('payments.modes.cash'), value: 'cash' },
  { label: t('payments.modes.card'), value: 'card' },
  { label: t('payments.modes.upi'), value: 'upi' },
  { label: t('payments.modes.cheque'), value: 'cheque' },
  { label: t('payments.modes.bank_transfer'), value: 'bank_transfer' },
])

// Form data
const formData = reactive({
  amount: null,
  payment_mode: 'cash',
  reference_number: '',
  notes: '',
})

// Form errors
const errors = reactive({
  amount: '',
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.bill) {
      formData.amount = amountDue.value > 0 ? amountDue.value : null
      formData.payment_mode = 'cash'
      formData.reference_number = ''
      formData.notes = ''
    } else {
      resetForm()
    }
    clearErrors()
  }
)

// Sync modal visibility back to parent
watch(showModal, (val) => {
  emit('update:show', val)
})

const clearErrors = () => {
  errors.amount = ''
}

const validateForm = () => {
  clearErrors()
  let isValid = true

  if (!formData.amount || formData.amount <= 0) {
    errors.amount = 'Amount must be greater than 0'
    isValid = false
  } else if (formData.amount > amountDue.value) {
    errors.amount = `Amount cannot exceed \u20B9${amountDue.value.toFixed(2)}`
    isValid = false
  }

  if (!formData.payment_mode) {
    isValid = false
  }

  return isValid
}

const resetForm = () => {
  Object.assign(formData, {
    amount: null,
    payment_mode: 'cash',
    reference_number: '',
    notes: '',
  })
  clearErrors()
}

const handleCancel = () => {
  showModal.value = false
  resetForm()
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    const paymentData = {
      purchase_bill_id: props.bill.id,
      amount: formData.amount,
      payment_mode: formData.payment_mode,
      reference_number: formData.reference_number || null,
      notes: formData.notes || null,
    }

    await createPurchasePayment(paymentData)

    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to record purchase payment:', error)
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.bill-details {
  margin-bottom: var(--space-6);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

.bill-detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-2);
}

.bill-detail-row:last-child {
  margin-bottom: 0;
}

.bill-detail-row .label {
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
}

.bill-detail-row .value {
  color: var(--color-charcoal);
  font-size: var(--font-size-sm);
}

.bill-detail-row.highlight .label,
.bill-detail-row.highlight .value {
  color: #d03050;
  font-weight: var(--font-weight-semibold);
}

.payment-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
</style>
